/**
 * Canonical section-id maps for the nested-route store editor.
 *
 * The store editor moved from hash navigation on the legacy flat route
 * (`/distributor/campaigns/:id#payment`) to real nested routes
 * (`/distributor/stores/:id/edit/:section`). This module is the single
 * source of truth for:
 *
 *   - SECTION_IDS: the ten editable section ids of the locked Phase-1 IA.
 *     The Review and Launch landing is represented by the empty section
 *     id `''` (the bare `/edit` URL), so it is NOT in this list.
 *
 *   - OLD_HASH_TO_SECTION: translates a legacy `#hash` value to its new
 *     section id, applying the Round-2 consolidations (name + client ->
 *     client-store, managers + departments -> team) and renames
 *     (payment -> pricing, tax -> tax-legal, login ->
 *     store-login, checkout -> checkout). Removed sections
 *     (review, fulfillment, locations) map to '' (the landing).
 *
 *   - OLD_TAB_TO_SECTION: translates a legacy /admin tab name to its new
 *     section id, for the `/admin/campaigns/:id/edit/:tab` redirect. Each
 *     new id is the destination of the FIRST legacy tab that fed it.
 *
 * PR-G (2026-06-22) Checkout IA refactor:
 *   - "checkout-fields" renamed to "checkout".
 *   - "pricing-checkout" renamed to "pricing".
 *   - Both legacy ids are preserved in OLD_HASH_TO_SECTION so existing
 *     URL hashes still resolve. The router also adds explicit redirects
 *     from the legacy nested-route paths /edit/checkout-fields ->
 *     /edit/checkout and /edit/pricing-checkout -> /edit/pricing.
 *
 * Keep these maps in lock-step with StoreEditShell.vue (sidebar ids) and
 * StoreAddEdit.vue (`_sectionIdToTabName`). Both are exercised by
 * `__tests__/section-routes.spec.js`.
 */

// The Review and Launch landing is the empty section id ''. The ten
// editable sections below are the locked Phase-1 IA: Setup, Catalog,
// Money and Checkout, People and Comms. Fulfillment is NOT a section.
// PR-G moved Checkout to Money and Checkout (renamed from "Checkout Fields"
// to plain "Checkout") and renamed "Pricing & Checkout" to "Pricing".
// C1 (2026-07-24, ADR 0059) added "categories" to the Catalog group,
// between "products" and "decorations", and the Catalog run was ordered
// to match the ADR 0059 locked design exactly -- Products, Categories &
// Storefront, Decorations (design/Locked Decisions.md "Chrome": "Catalog
// (Products - Categories & Storefront - Decorations)"). This flips the
// prior Decorations-then-Products order per Jake's confirmation that the
// nav must match the design. See
// docs/planning/2026-07-23-store-categories-implementation-plan.md.
// 2026-08-20 (ADR 0066): "batches" joins the Money & Checkout group. The
// Locked Decisions for the Batch Management handoff name the tab "Batches",
// and ADR 0066 decision 7 puts the per-store batching rules on the batch
// surface itself rather than in a settings section, because "the rules
// explain the list". Same shape of addition as "defaults" (Phase 4.11) and
// "categories" (ADR 0059): a design-authored section, not an ad-hoc one.
export const SECTION_IDS = Object.freeze([
  "client-store",
  "branding",
  "store-login",
  "defaults",
  "products",
  "categories",
  "decorations",
  "pricing",
  "checkout",
  "batches",
  "tax-legal",
  "team",
  "notifications",
]);

// Canonical title + Material icon per section. Single source of truth for
// every surface that lists "all editable sections": the StoreEditShell
// sidebar groups and the StoreReviewLaunch landing grid.
// Order matches SECTION_IDS exactly -- both lists are kept in lock-step.
//
// 2026-08-12 (Jake): this list carries NO group concept -- it is flat
// {id,title,icon} metadata, and the StoreReviewLaunch grid renders it as a
// flat, header-less "All sections" card list. The sidebar group ORDER lives
// solely in StoreEditShell.vue::sectionGroups, which moved Money & Checkout
// above Catalog on this date. This flat order was deliberately NOT
// re-sequenced to match: doing so would re-order a second user-visible
// surface (the landing grid) that the change did not ask to touch. So the
// id->title->icon mapping is still authoritative and in lock-step, but the
// flat SEQUENCE here no longer mirrors top-to-bottom sidebar order.
//
// When adding or renaming a section, edit BOTH SECTION_IDS and SECTION_META
// here, then verify StoreEditShell.vue::sectionGroups carries the same
// id->title->icon for that section.
export const SECTION_META = Object.freeze([
  // 2026-08-12 (Jake): renamed from "Client & Store" (title only -- the id
  // stays 'client-store', so URLs and route lookups are unchanged).
  { id: "client-store",     title: "Client & Store Details", icon: "event" },
  { id: "branding",         title: "Branding & Home Page", icon: "palette" },
  { id: "store-login",      title: "Store Login",        icon: "vpn_key" },
  { id: "defaults",         title: "Defaults",           icon: "auto_awesome" },
  { id: "products",         title: "Products",           icon: "inventory_2" },
  { id: "categories",       title: "Categories & Storefront", icon: "storefront" },
  { id: "decorations",      title: "Decorations",        icon: "design_services" },
  { id: "pricing",          title: "Pricing",            icon: "credit_card" },
  { id: "checkout",         title: "Checkout",           icon: "list_alt" },
  { id: "batches",          title: "Batches",            icon: "inventory" },
  { id: "tax-legal",        title: "Tax & Legal",        icon: "gavel" },
  { id: "team",             title: "Team & Departments", icon: "supervisor_account" },
  { id: "notifications",    title: "Email Notifications", icon: "mail" },
]);

// Legacy hash value (without the leading '#') -> new section id.
// '' is the Review and Launch landing.
//
// PR-G also tolerates the immediately-superseded section ids
// ("checkout-fields", "pricing-checkout") as hash keys so any URL or
// bookmark using those identifiers continues to resolve to the new
// canonical sections.
export const OLD_HASH_TO_SECTION = Object.freeze({
  // Setup: name + client merge into one Client and Store section.
  name: "client-store",
  client: "client-store",
  branding: "branding",
  login: "store-login",
  checkout: "checkout",

  // Catalog (ordered Products, Categories & Storefront, Decorations per
  // ADR 0059 -- key order here is non-functional identity mapping, kept
  // tidy). No legacy hash exists for the net-new "categories" section.
  products: "products",
  decorations: "decorations",

  // Money and Checkout: payment renamed, tax renamed.
  payment: "pricing",
  tax: "tax-legal",

  // PR-G: tolerate the prior section ids as URL hash inputs.
  "checkout-fields": "checkout",
  "pricing-checkout": "pricing",

  // People and Comms: managers + departments merge into one Team section.
  managers: "team",
  departments: "team",
  notifications: "notifications",

  // Landing and removed sections all resolve to the landing.
  review: "",
  fulfillment: "",
  locations: "",
});

// Legacy /admin tab name -> new section id. Each new id is fed by the
// FIRST legacy tab in document order; later tabs that consolidated into
// the same section also map here so the redirect never 404s.
export const OLD_TAB_TO_SECTION = Object.freeze({
  campaignSetup: "client-store",
  clientContact: "client-store",
  preview: "branding",
  shopper: "store-login",
  checkoutInfo: "checkout",
  features: "pricing",
  departments: "team",
  managers: "team",
  products: "products",
  decorations: "decorations",
  emailConfig: "notifications",

  // Inert / removed legacy tabs resolve to the landing.
  locations: "",
  fulfillment: "",
  orders: "",
  reports: "",
});

export default {
  SECTION_IDS,
  SECTION_META,
  OLD_HASH_TO_SECTION,
  OLD_TAB_TO_SECTION,
};
